import {
  BriefcaseIcon,
  EnvelopeIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuthSession } from 'src/auth/hooks/useAuthSession';

const WORKSPACE_ADMIN_ROLES = [
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'TENANT_MANAGER',
  'COMPANY_ADMIN',
  'COMPANY_MANAGER',
];

const EMAIL_TEMPLATE_EDITOR_ROLES = ['TENANT_OWNER', 'TENANT_ADMIN'];

const SettingsLayout: React.FC = () => {
  const { workspaceContext } = useAuthSession();

  const canViewWorkspaceUsers =
    workspaceContext?.roles.some((r) => WORKSPACE_ADMIN_ROLES.includes(r)) ??
    false;

  const canManageEmailTemplates =
    workspaceContext?.roles.some((r) =>
      EMAIL_TEMPLATE_EDITOR_ROLES.includes(r)
    ) ?? false;

  const navItems = [
    {
      name: 'Profile',
      to: '/settings/profile',
      icon: UserIcon,
      show: canViewWorkspaceUsers,
    },
    {
      name: 'Workspaces',
      to: '/settings/workspaces',
      icon: BriefcaseIcon,
      show: canViewWorkspaceUsers,
    },
    {
      name: 'Users',
      to: '/settings/workspace/users',
      icon: UsersIcon,
      show: canViewWorkspaceUsers,
    },
    {
      name: 'Email Templates',
      to: '/settings/workspace/email-templates',
      icon: EnvelopeIcon,
      show: canManageEmailTemplates,
    },
  ];

  const navLinkClass = (isActive: boolean) =>
    `flex items-center justify-center md:justify-start gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-100 text-primary-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <div className="flex min-h-full w-full max-w-full overflow-hidden">
      <nav className="md:w-48 w-20 shrink-0 border-r border-gray-200 p-4 space-y-2 md:space-y-1">
        <p className="md:text-xs text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
          Settings
        </p>
        {navItems
          .filter((item) => item.show)
          .map((item) => {
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => navLinkClass(isActive)}
              >
                <item.icon className="w-5 h-5 shrink-0" />
                <span className="hidden md:block">{item.name}</span>
              </NavLink>
            );
          })}
      </nav>
      <div className="flex-1 min-w-0 w-full overflow-hidden p-4 md:p-6">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
